/**
 * Client Portal V2 Dashboard Page
 * ================================
 * Full dashboard with JWT authentication and proper error handling.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import { 
  Building2, Briefcase, Calendar, FileText, Clock, AlertTriangle, 
  CheckCircle, RefreshCw, LogOut, ChevronRight, BarChart3,
  Shield, FileCheck
} from "lucide-react";
import { toast } from "sonner";

function StatCard({ title, value, icon: Icon, color = "teal", trend, onClick }) {
  const colorClasses = {
    teal: "from-teal-500 to-teal-600",
    blue: "from-blue-500 to-blue-600",
    amber: "from-amber-500 to-amber-600",
    red: "from-red-500 to-red-600",
    green: "from-green-500 to-green-600",
    purple: "from-purple-500 to-purple-600",
  };

  return (
    <div 
      className={`relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition-all hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{title}</p>
          <p className="mt-2 text-3xl font-bold text-slate-900">{value ?? 0}</p>
          {trend && (
            <p className={`mt-1 text-xs ${trend > 0 ? 'text-green-600' : 'text-slate-400'}`}>
              {trend > 0 ? `+${trend}` : trend} from last month
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {onClick && (
        <div className="absolute bottom-3 right-3">
          <ChevronRight className="w-4 h-4 text-slate-300" />
        </div>
      )}
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, description }) {
  return (
    <Link 
      to={to}
      className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-100 hover:border-teal-200 hover:shadow-md transition-all group"
      data-testid={`quick-link-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="p-3 rounded-lg bg-slate-100 group-hover:bg-teal-50 transition-colors">
        <Icon className="w-5 h-5 text-slate-600 group-hover:text-teal-600 transition-colors" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-sm text-slate-500 truncate">{description}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
    </Link>
  );
}

export default function ClientDashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const res = await apiClient.get("/api/system-expansion/client-v2/dashboard");
      setData(res.data);
      
      // Update stored client info
      if (res.data?.client) {
        localStorage.setItem("client_info", JSON.stringify(res.data.client));
      }
    } catch (err) {
      console.error("Dashboard load error:", err);

      if (err.response?.status === 401) {
        clientAuth.logout();
        return;
      }

      setError("Unable to load dashboard data. Please try again.");
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }
    loadDashboard();
  }, [navigate, loadDashboard]);

  const handleLogout = () => {
    clientAuth.logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="client-dashboard-loading">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-teal-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="client-dashboard-error">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Dashboard</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={loadDashboard}
              className="px-6 py-2.5 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={handleLogout}
              className="px-6 py-2.5 rounded-xl bg-slate-200 text-slate-700 font-medium hover:bg-slate-300 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  const brandName = data?.brand?.name || "Client Portal";
  const clientName = data?.client?.name || "Client";
  const stats = data?.stats || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">{brandName}</h1>
                <p className="text-xs text-slate-500">Client Portal</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={loadDashboard}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-slate-600" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition-colors"
                data-testid="client-logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-teal-700 p-6 mb-8 text-white shadow-lg shadow-teal-600/20">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold">Welcome back, {clientName}</h2>
              <p className="mt-1 text-teal-100">Here's an overview of your operations</p>
            </div>
            {data?.authenticated && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-sm">
                <CheckCircle className="w-4 h-4" />
                Authenticated
              </div>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard 
            title="Total Jobs" 
            value={stats.total_jobs} 
            icon={Briefcase}
            color="teal"
            onClick={() => navigate("/client-v2/jobs")}
          />
          <StatCard 
            title="Open Jobs" 
            value={stats.open_jobs} 
            icon={Clock}
            color="blue"
            onClick={() => navigate("/client-v2/jobs")}
          />
          <StatCard 
            title="PPM Total" 
            value={stats.total_ppm} 
            icon={Calendar}
            color="purple"
            onClick={() => navigate("/client-v2/ppm")}
          />
          <StatCard 
            title="PPM Overdue" 
            value={stats.overdue_ppm} 
            icon={AlertTriangle}
            color={stats.overdue_ppm > 0 ? "red" : "green"}
          />
          <StatCard 
            title="Quotes Awaiting" 
            value={stats.quotes_awaiting} 
            icon={FileText}
            color="amber"
            onClick={() => navigate("/client-v2/quotes")}
          />
        </div>

        {/* Quick Links */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickLink 
              to="/client-v2/jobs" 
              icon={Briefcase} 
              label="View Jobs" 
              description="All reactive and PPM jobs"
            />
            <QuickLink 
              to="/client-v2/ppm" 
              icon={Shield} 
              label="PPM Compliance" 
              description="Scheduled maintenance status"
            />
            <QuickLink 
              to="/client-v2/quotes" 
              icon={FileCheck} 
              label="Approve Quotes" 
              description="Pending quote approvals"
            />
            <QuickLink 
              to="/client/dashboard" 
              icon={BarChart3} 
              label="Billing Dashboard" 
              description="Invoices and payments"
            />
          </div>
        </div>

        {/* Status Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compliance Status */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Compliance Status</h3>
            <div className="space-y-3">
              {stats.overdue_ppm === 0 ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-100">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">All Compliant</p>
                    <p className="text-sm text-green-600">No overdue maintenance items</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">{stats.overdue_ppm} Overdue Items</p>
                    <p className="text-sm text-red-600">Requires immediate attention</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Active Jobs</span>
                <span className="font-semibold text-slate-900">{stats.open_jobs || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">PPM Schedules</span>
                <span className="font-semibold text-slate-900">{stats.total_ppm || 0}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-slate-600">Pending Approvals</span>
                <span className="font-semibold text-slate-900">{stats.quotes_awaiting || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
